import { useEffect, useMemo } from "react";

import { isInProductJourneySubAnchor } from "src/pages/software/confluence/anchors/subAnchors/subAnchor-util";

import { AnalyticsContext } from "@atlaskit/analytics-next";

import { ProductKey } from "@/components/signup/utils";
import { useUserContext } from "@/components/user-context";
import { useGupsStatus } from "@/templates/ondemand/signup/confirmation/confirmation-page/src/hooks";
import { PerceivedProvisioningStartTimer } from "@/templates/ondemand/signup/confirmation/confirmation-page/src/utils/perceived-provisioning/perceived-provisioning-start-timer.tsx";
import { DataFoundationsWrapper } from "@/utils/data-foundations/data-foundation-types";
import { useFeatureExposed } from "@/utils/feature-exposed";
import { useLocation } from "@/utils/location-provider";

import { useSubanchorContext } from "../../hooks";

import { getLayout } from "./constants";
import { ConfluenceLayout } from "./layouts/confluence";
import { JiraProductDiscoveryLayout } from "./layouts/jira-product-discovery";
import { SignupProvisioningContextProvider } from "./signup-provisioning-context";

import("@apollo/client/dev")
    .then(({ loadErrorMessages, loadDevMessages }) => {
        loadErrorMessages();
        loadDevMessages();
    })
    .catch((error) => {
        console.error("Error loading Apollo client devtools:", error);
    });

export const SignupProvisioningModule = () => {
    const { linkAccountToAnonymousIdCallback } = useGupsStatus();
    const { subAnchor } = useSubanchorContext();
    const hasTargetedSubAnchor = isInProductJourneySubAnchor(subAnchor);

    useEffect(() => {
        linkAccountToAnonymousIdCallback();
    }, [linkAccountToAnonymousIdCallback]);

    const location = useLocation();

    const searchParameters = new URLSearchParams(location.search);
    const products = useMemo(
        () => (searchParameters.get("products") ?? "").split(","),
        [location.search]
    );
    const { profile } = useUserContext();
    const sites = profile.attributes?.existingSites;
    const hasOnlyConfluenceSites = sites
        ? sites.every((site) => {
              const products = site.products;
              return products.every(
                  ({ productName }) => productName === "confluence"
              );
          })
        : true;
    const isXflow = !hasOnlyConfluenceSites;
    const edition: string | undefined =
        searchParameters.get("edition") ?? undefined;
    const templateId = searchParameters.get("templateId") ?? undefined;
    const defaultAnalyticsAttributes = {
        schemaVersion: 1,
        event: "event", // event verb, in past tense (eg, "clicked")
        eventType: "ui",
        eventComponent: "signUpProvisioningModule", // name of component being interacted with, in camelCase
        eventContainer: "signUpConfirmationPage", // container, in camelCase; eg, component that contains this component within a page
        eventColumn: "signUpConfirmationPage", // should be bundleInformation or signUpForm
        eventName: "signUpConfirmationPageEvent",
        label: "", // the text present on the component being clicked (exact text, not camelCase)
        parentLabel: "", // optional, if this is a nested click (exact text, not camelCase)
        uuid: "signUpConfirmationPage", // required for Magnolia events, unique id for a given component if available
        products
    };
    const productKey = useMemo(
        () => (products.length > 0 ? products[0] : undefined) as ProductKey,
        [products]
    );
    const dataFoundationAttributes = {
        productKey,
        edition
    };

    const layout = useMemo(() => getLayout(products), [products]);

    const isEligibleForTailorSubAnchorJourneyExp =
        !isXflow &&
        !templateId &&
        hasTargetedSubAnchor &&
        layout === "confluence";

    useFeatureExposed(
        "cfind-3233-in-product-sub-anchors-journeys",
        "enrolled",
        { conditions: [isEligibleForTailorSubAnchorJourneyExp] }
    );

    // this module is only used for confluence and jira product discovery currently
    const content = useMemo(() => {
        if (layout === "confluence") {
            return <ConfluenceLayout />;
        }
        return <JiraProductDiscoveryLayout />;
    }, [layout]);

    return (
        <AnalyticsContext data={defaultAnalyticsAttributes}>
            <DataFoundationsWrapper
                dataFoundationsEventOption="ProvisioningScreenViewed"
                attributes={dataFoundationAttributes}
            >
                <SignupProvisioningContextProvider>
                    <PerceivedProvisioningStartTimer>
                        {content}
                    </PerceivedProvisioningStartTimer>
                </SignupProvisioningContextProvider>
            </DataFoundationsWrapper>
        </AnalyticsContext>
    );
};

export default SignupProvisioningModule;
